import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import service from '../../services/api/supportMirrorService';
import { safeMirrorHtml, restoreMirrorScroll } from '../../components/support/mirrorDocument';

export default function SupportMirror() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [frame, setFrame] = useState(null);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [availableWidth, setAvailableWidth] = useState(900);
  const container = useRef(null);
  const iframe = useRef(null);
  const revision = useRef(-1);
  const pendingStop = useRef(null);
  const html = useMemo(() => frame ? safeMirrorHtml(frame.html) : '', [frame]);
  useEffect(() => {
    let cancelled = false; let timer;
    // StrictMode may set up the same effect again immediately. Cancel only
    // that transient cleanup, while still ending a genuinely previous ID.
    if (pendingStop.current?.id === id) clearTimeout(pendingStop.current.timer);
    setFrame(null); setSession(null); setError('');
    revision.current = -1;
    const poll = async () => {
      try {
        const data = await service.view(id, revision.current);
        if (cancelled) return;
        setSession(data); setError(''); setNow(Date.now());
        if (data.status === 'ended') { setFrame(null); return; }
        if (data.frame) { setFrame({ ...data.frame, receivedAt: Date.now() }); revision.current = data.revision; }
        if (data.stale) { setFrame(null); revision.current = -1; }
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.error?.message || 'Mirror connection lost.'); setFrame(null); revision.current = -1;
      }
      if (!cancelled) timer = setTimeout(poll, 500);
    };
    poll();
    return () => {
      cancelled = true; clearTimeout(timer);
      pendingStop.current = { id, timer: setTimeout(() => service.stop(id).catch(() => {}), 0) };
    };
  }, [id]);
  useEffect(() => {
    const resize = () => { if (container.current) setAvailableWidth(container.current.clientWidth); };
    resize(); window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  const stop = async () => {
    try { await service.stop(id); setSession((previous) => ({ ...previous, status: 'ended' })); setFrame(null); }
    catch (err) { setError(err.response?.data?.error?.message || err.message); }
  };
  const scale = frame ? Math.min(1, availableWidth / frame.width) : 1;
  return <section>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
      <h1>Support Mirror</h1>
      <Link to="/support">Back to support</Link>
      {session?.status !== 'ended' && <button type="button" onClick={stop}>Stop mirror</button>}
    </div>
    <p role="status">{session?.status === 'ended' ? 'Mirror session ended.' : session?.status === 'pending' ? 'Waiting for the user to open Tempus and select “Share Tempus page”. The request appears within 5 seconds.' : frame ? 'Live view · cursor and mouse clicks are shown · updates approximately every 1–2 seconds' : 'Waiting for the shared browser tab. No current view is available.'}</p>
    <p>View only. The user controls their page. Passwords are hidden. Browser dialogs, other tabs and external embedded content are not shared.</p>
    {error && <p role="alert" style={{ color: '#b91c1c' }}>{error}</p>}
    <div ref={container} style={{ width: '100%', overflow: 'hidden', border: '1px solid #aab8c4', borderRadius: 6, background: '#f8fafc', minHeight: 100, height: frame ? frame.height * scale : undefined }}>
      {frame && <div style={{ width: frame.width, height: frame.height, transform: `scale(${scale})`, transformOrigin: 'top left', position: 'relative', pointerEvents: 'none' }}>
        <iframe ref={iframe} title="Live user Tempus page" sandbox="allow-same-origin" referrerPolicy="no-referrer" srcDoc={html} onLoad={() => restoreMirrorScroll(iframe.current, frame)} tabIndex={-1} style={{ border: 0, width: frame.width, height: frame.height, pointerEvents: 'none' }} />
        {frame.pointer && <span aria-label="User cursor" style={{ position: 'absolute', left: Number(frame.pointer.x) || 0, top: Number(frame.pointer.y) || 0, color: '#dc2626', fontSize: 25, lineHeight: 1, textShadow: '0 0 3px white' }}>↖</span>}
        {(frame.clicks || []).filter(() => now - frame.receivedAt < 2500).map((click, index) => <span key={`${click.at}-${index}`} aria-label={click.button === 2 ? 'Right mouse click' : 'Mouse click'} style={{ position: 'absolute', left: (Number(click.x) || 0) - 14, top: (Number(click.y) || 0) - 14, width: 28, height: 28, borderRadius: '50%', border: '3px solid #dc2626', background: '#fca5a544' }} />)}
      </div>}
    </div>
  </section>;
}
